import { useState, useEffect, useRef } from 'react';
import { HiPlus, HiPencil, HiTrash, HiDocumentDownload, HiUpload, HiX, HiCheckCircle } from 'react-icons/hi';
import { cataloguesService, sizesService, uploadService } from '../api/services';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';

// Base URL without /api/v1 for serving files
const FILE_BASE = import.meta.env.VITE_API_URL?.replace('/api/v1', '') || 'http://localhost:8000';

export default function Catalogues() {
  const [catalogues, setCatalogues] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCatalogue, setEditingCatalogue] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(''); // Status message during upload
  const [formData, setFormData] = useState({
    name: '',
    thumbnail_url: '',
    pdf_url: '',
    size_id: '',
    is_active: true,
  });

  const pdfInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);

  useEffect(() => {
    fetchCatalogues();
    fetchSizes();
  }, []);

  const fetchCatalogues = async () => {
    try {
      const response = await cataloguesService.getAll();
      setCatalogues(response.data);
    } catch (error) {
      toast.error('Failed to load catalogues');
    } finally {
      setLoading(false);
    }
  };

  const fetchSizes = async () => {
    try {
      const response = await sizesService.getAll();
      setSizes(response.data);
    } catch (error) {
      console.error('Failed to load sizes');
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }

    // Check file size (100MB limit)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 100MB');
      return;
    }

    setUploading(true);
    setUploadStatus('Uploading PDF...');
    try {
      setUploadStatus('Uploading & compressing PDF...');
      const response = await uploadService.uploadPdf(file, true);
      const data = response.data;

      setUploadStatus('Processing complete!');

      // Set PDF URL
      setFormData(prev => ({
        ...prev,
        pdf_url: data.pdf_url,
        // Auto-set thumbnail if generated and no existing thumbnail
        thumbnail_url: data.thumbnail_url || prev.thumbnail_url
      }));

      // Show compression info
      if (data.compression_ratio) {
        toast.success(`PDF uploaded & compressed (${data.compression_ratio} saved)!`);
      } else {
        toast.success('PDF uploaded successfully!');
      }

      // Show thumbnail generation status
      if (data.thumbnail_url) {
        toast.success('Thumbnail generated from first page!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setUploading(false);
      setUploadStatus('');
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadService.uploadThumbnail(file);
      setFormData(prev => ({
        ...prev,
        thumbnail_url: response.data.url
      }));
      toast.success('Thumbnail uploaded!');
    } catch (error) {
      toast.error('Failed to upload thumbnail');
    } finally {
      setUploading(false);
    }
  };

  const getFullUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${FILE_BASE}${path}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!formData.pdf_url) {
      toast.error('Please upload a PDF file');
      return;
    }
    if (!formData.thumbnail_url) {
      toast.error('Thumbnail is required. Upload a PDF to auto-generate or upload manually.');
      return;
    }
    if (!formData.size_id) {
      toast.error('Please select a size');
      return;
    }

    try {
      const data = {
        ...formData,
        size_id: parseInt(formData.size_id),
      };

      if (editingCatalogue) {
        await cataloguesService.update(editingCatalogue.id, data);
        toast.success('Catalogue updated successfully');
      } else {
        await cataloguesService.create(data);
        toast.success('Catalogue created successfully');
      }

      setShowModal(false);
      resetForm();
      fetchCatalogues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (catalogue) => {
    setEditingCatalogue(catalogue);
    setFormData({
      name: catalogue.name || '',
      thumbnail_url: catalogue.thumbnail_url || '',
      pdf_url: catalogue.pdf_url || '',
      size_id: catalogue.size_id || '',
      is_active: catalogue.is_active ?? true,
    });
    setShowModal(true);
  };

  const handleDelete = async (catalogue) => {
    if (window.confirm(`Are you sure you want to delete "${catalogue.name}"?`)) {
      try {
        await cataloguesService.delete(catalogue.id);
        toast.success('Catalogue deleted successfully');
        fetchCatalogues();
      } catch (error) {
        const errorMsg = error.response?.data?.detail || 'Failed to delete catalogue';
        // Show more prominent error for linked enquiries
        if (errorMsg.includes('enquiry') || errorMsg.includes('linked')) {
          toast.error(errorMsg, { duration: 5000 });
        } else {
          toast.error(errorMsg);
        }
        console.error('Delete error:', error.response?.data);
      }
    }
  };

  const resetForm = () => {
    setEditingCatalogue(null);
    setFormData({
      name: '',
      thumbnail_url: '',
      pdf_url: '',
      size_id: '',
      is_active: true,
    });
    setUploadStatus('');
    if (pdfInputRef.current) pdfInputRef.current.value = '';
    if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
  };

  const columns = [
    {
      key: 'thumbnail',
      label: 'Thumbnail',
      render: (row) => (
        <div className="w-16 h-16 rounded-lg bg-dark-400 overflow-hidden">
          {row.thumbnail_url ? (
            <img src={getFullUrl(row.thumbnail_url)} alt={row.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">
              No image
            </div>
          )}
        </div>
      ),
    },
    { key: 'name', label: 'Name' },
    {
      key: 'size',
      label: 'Size',
      render: (row) => row.size ? (
        <span className="text-gold-500 font-medium">{row.size.name}</span>
      ) : (
        <span className="text-gray-500">-</span>
      ),
    },
    {
      key: 'pdf',
      label: 'PDF',
      render: (row) => row.pdf_url ? (
        <a
          href={getFullUrl(row.pdf_url)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-gold-500 hover:text-gold-400"
          onClick={(e) => e.stopPropagation()}
        >
          <HiDocumentDownload className="w-5 h-5" />
          <span className="text-sm">View</span>
        </a>
      ) : (
        <span className="text-gray-500">-</span>
      ),
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (row) => (
        <span className={`status-badge ${row.is_active ? 'bg-green-900/50 text-green-300 border border-green-700' : 'bg-gray-800 text-gray-400 border border-gray-600'}`}>
          {row.is_active ? 'ACTIVE' : 'INACTIVE'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
            className="p-2 text-gray-400 hover:text-gold-500 transition-colors"
          >
            <HiPencil className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
          >
            <HiTrash className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold gold-gradient-text">Catalogues</h1>
          <p className="text-gray-400 mt-1">Manage product catalogues</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-primary flex items-center gap-2"
        >
          <HiPlus className="w-5 h-5" />
          Add Catalogue
        </button>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold-500"></div>
          </div>
        ) : (
          <DataTable columns={columns} data={catalogues} />
        )}
      </div>

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editingCatalogue ? 'Edit Catalogue' : 'Add New Catalogue'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input-field"
              placeholder="Catalogue name"
              required
            />
          </div>

          {/* PDF Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">PDF File <span className="text-red-400">*</span></label>
            <div className="space-y-2">
              <input
                ref={pdfInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handlePdfUpload}
                className="hidden"
                id="pdf-upload"
              />
              <label
                htmlFor="pdf-upload"
                className={`flex items-center justify-center gap-2 px-4 py-3 border border-dashed border-dark-300 rounded-lg cursor-pointer hover:border-gold-500/50 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gold-500"></div>
                    <span className="text-sm text-gold-500">{uploadStatus || 'Processing...'}</span>
                  </>
                ) : (
                  <>
                    <HiUpload className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-400">Click to upload PDF (max 100MB)</span>
                  </>
                )}
              </label>
              {formData.pdf_url && (
                <div className="flex items-center gap-2 p-3 bg-green-900/20 border border-green-700/50 rounded-lg">
                  <HiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-green-400 font-medium">PDF uploaded successfully!</p>
                    <a
                      href={getFullUrl(formData.pdf_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-gold-500 hover:text-gold-400 truncate block"
                    >
                      Click to view PDF
                    </a>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, pdf_url: '' })}
                    className="p-1 text-gray-400 hover:text-red-400 flex-shrink-0"
                    title="Remove PDF"
                  >
                    <HiX className="w-4 h-4" />
                  </button>
                </div>
              )}
              <p className="text-xs text-gray-500">PDF will be compressed automatically. Thumbnail will be generated from first page.</p>
            </div>
          </div>

          {/* Thumbnail Preview & Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Thumbnail <span className="text-red-400">*</span></label>
            <div className="space-y-2">
              {/* Thumbnail Preview */}
              {formData.thumbnail_url && (
                <div className="relative inline-block">
                  <div className="w-32 h-40 rounded-lg overflow-hidden bg-dark-500 border border-dark-400">
                    <img
                      src={getFullUrl(formData.thumbnail_url)}
                      alt="Thumbnail preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full items-center justify-center text-gray-500 text-xs hidden">
                      Failed to load
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, thumbnail_url: '' })}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                  >
                    <HiX className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* Upload Button */}
              <div>
                <input
                  ref={thumbnailInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  className="hidden"
                  id="thumbnail-upload"
                />
                <label
                  htmlFor="thumbnail-upload"
                  className={`inline-flex items-center gap-2 px-4 py-2 border border-dark-300 rounded-lg cursor-pointer hover:border-gold-500/50 transition-colors text-sm ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  <HiUpload className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-400">
                    {formData.thumbnail_url ? 'Replace thumbnail' : 'Upload custom thumbnail'}
                  </span>
                </label>
              </div>
              {!formData.thumbnail_url && (
                <p className="text-xs text-gray-500">Thumbnail will be auto-generated when you upload a PDF</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Size <span className="text-red-400">*</span></label>
            <select
              value={formData.size_id}
              onChange={(e) => setFormData({ ...formData, size_id: e.target.value })}
              className="input-field"
              required
            >
              <option value="">Select Size</option>
              {sizes.filter(s => s.is_active).map((size) => (
                <option key={size.id} value={size.id}>{size.name}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-dark-300 bg-dark-500 text-gold-500 focus:ring-gold-500"
              />
              <span className="text-sm text-gray-300">Active</span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="submit" className="btn-primary flex-1" disabled={uploading}>
              {editingCatalogue ? 'Update Catalogue' : 'Create Catalogue'}
            </button>
            <button
              type="button"
              onClick={() => { setShowModal(false); resetForm(); }}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
